import React, { useState, useRef, useMemo } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import {
  InputElementFragment,
  FormInfoFragment,
  PublicFormElementInput,
  FormPrefillEntryFragment,
  ExpirySettingsFragment,
} from "__generated__/graphqlUnauthenticated";
import { PreviewFormItem } from "./PreviewFormItem";
import { Form, Formik } from "formik";
import { mapToInitialValues } from "./helpers/mapToInitialValues";
import { ConfirmationPage } from "./ConfirmationPage";
import { Loading } from "common/state/Loading";
import ReCAPTCHA from "react-google-recaptcha";
import { generateValidationSchema } from "./generateValidationSchema";
import { CoverPhotoPreview } from "../builder/main/coverPhoto/CoverPhotoPreview";
import { useLocation } from "react-router-dom";
import { ExpiryMessagePage } from "./ExpiryMessagePage";

// hiding these for v1.0 since they require CRM lookup
export const hideTypes = [
  "EMPLOYEE",
  "MULTI_COMPANY",
  "MULTI_CONTACT",
  "MULTI_EMPLOYEE",
  "CURRENCY",
];
interface IPreviewFormUIProps {
  loading: boolean;
  formDetails: FormInfoFragment | null;
  prefill: FormPrefillEntryFragment[];
  onSubmit: (values: any) => Promise<{ error?: string }>;
  isDraft: boolean;
  errorMessage: string | undefined;
  setRecaptchaToken: (token: string) => void;
  loadingFormInfo: boolean;
  isExpired: boolean;
  expirySettings: ExpirySettingsFragment | null | undefined;
  ff_release_13_form_footer_links_new_tab?: boolean;
}

export const PreviewFormUI: React.FC<IPreviewFormUIProps> = ({
  onSubmit,
  formDetails,
  isDraft,
  setRecaptchaToken,
  errorMessage,
  loading,
  prefill,
  loadingFormInfo,
  isExpired,
  expirySettings,
  ff_release_13_form_footer_links_new_tab,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const captchaRef = useRef<any>(null);

  const footer = useMemo(() => {
    if (!ff_release_13_form_footer_links_new_tab)
      return formDetails?.defaultFooter || "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      formDetails?.defaultFooter || "",
      "text/html",
    );
    doc.querySelectorAll("a").forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });
    return doc.body.innerHTML;
  }, [formDetails?.defaultFooter, !ff_release_13_form_footer_links_new_tab]);

  const handleOnSubmit = (values: any) => {
    const inputs: PublicFormElementInput[] = [];
    for (const key in values) {
      inputs.push({ elementId: key, value: values[key] });
    }
    onSubmit(inputs).then((result) => {
      if (!result.error) {
        // send message to parent window so clients can listen to the event and build custom logic
        window.parent.postMessage({ type: "formSubmitted" }, "*");
        setIsSubmitted(true);
      }
      captchaRef?.current?.reset();
    });
  };
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  if (!formDetails || loadingFormInfo)
    return (
      <Box height={"100vh"}>
        <Loading copy="Loading Form..." />
      </Box>
    );
  if (isExpired) {
    return (
      <ExpiryMessagePage
        header={expirySettings?.pageHeader || "Form Has Expired"}
        message={expirySettings?.pageDescription?.htmlForEditor}
      />
    );
  }
  if (isSubmitted)
    return (
      <ConfirmationPage
        confirmationPageSettings={formDetails.confirmationPageSettings}
      />
    );
  // Don't render form if waitlist redirect is required
  if (formDetails.waitlistSettings?.redirectRequired) {
    return (
      <Box height={"100vh"}>
        <Loading copy="Redirecting..." />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        overflow: "scroll",
        display: "flex",
        justifyContent: "center",
        background: (theme) => theme.palette.background.lightBlue,
      }}
      data-cy="preview-form"
      className="nexl-form-wrapper" //required to customize the form theme from form general settings in the topbar admin
    >
      <Box
        sx={{
          width: { xs: "100%", md: 680 },
          p: 4,
          backgroundColor: (theme) => ` ${theme.palette.common.white}`,
        }}
      >
        {formDetails.theme.coverImage?.url && (
          <Box sx={{ mb: 2 }}>
            <CoverPhotoPreview imageUrl={formDetails.theme.coverImage.url} />
          </Box>
        )}
        <Typography variant="h4">{formDetails.title}</Typography>
        <Box>
          <Box my={2}>
            <Typography>{formDetails.description}</Typography>
          </Box>
          <Box my={2}>
            <Formik
              initialValues={mapToInitialValues(
                (formDetails.elements || []) as InputElementFragment[],
                prefill,
                urlParams,
              )}
              validationSchema={generateValidationSchema(
                (formDetails.elements || []) as InputElementFragment[],
              )}
              onSubmit={handleOnSubmit}
            >
              {({ isValid }) => {
                return (
                  <Form>
                    {formDetails.elements.map((element: any, idx) => {
                      if (
                        (element.displayAs &&
                          hideTypes.includes(element.displayAs)) ||
                        element.hidden
                      )
                        return null;
                      return (
                        <>
                          {element.__typename === "InputElement" ? (
                            <PreviewFormItem
                              key={idx}
                              formElement={element as InputElementFragment}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              dangerouslySetInnerHTML={{
                                __html: element.value,
                              }}
                            />
                          )}
                        </>
                      );
                    })}
                    <Typography>
                      <div dangerouslySetInnerHTML={{ __html: footer }} />
                    </Typography>
                    <Box mt={1} />
                    {!formDetails.disableRecaptcha && (
                      <ReCAPTCHA
                        sitekey="6Lf3VsYnAAAAALzPx9DbJd9q91p0errhZGXT5jYX"
                        onChange={setRecaptchaToken}
                        ref={captchaRef}
                      />
                    )}
                    <Box>
                      {(!isValid || errorMessage) && (
                        <Typography color="error" variant="caption">
                          {!isValid
                            ? "Required fields are empty"
                            : errorMessage}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="medium"
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={isDraft || loading}
                      endIcon={loading && <CircularProgress size="16px" />}
                      sx={{ mt: 1 }}
                    >
                      Submit
                    </Button>
                  </Form>
                );
              }}
            </Formik>
          </Box>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography variant="caption" color="grey.900">
              Supported by
            </Typography>
            <img height={16} src="/images/nexl_logo.svg" alt="go to home" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
